import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { label: "メッセージ", path: "/recruit/message" },
  { label: "ACOEについて", path: "/recruit/about" },
  { label: "仕事について", path: "/recruit/work" },
  { label: "制度・環境", path: "/recruit/culture" },
  { label: "募集要項", path: "/recruit/careers" },
  { label: "採用の流れ", path: "/recruit/process" },
  { label: "5分でわかる", path: "/recruit/summary" },
];

// ScrollToTop component to handle navigation scroll
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

export default function RecruitNavigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/recruit" || location.pathname === "/recruit/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setIsOpen(false); }, [location.pathname]);

  const navBg = scrolled || !isHome
    ? "bg-acoe-bg/90 nav-blur border-b border-acoe-border"
    : "bg-transparent";

  const textColor = scrolled || !isHome ? "text-acoe-charcoal" : "text-white";

  return (
    <>
      <ScrollToTop />
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}
        data-testid="navigation-header"
      >
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-12 h-16 md:h-20">
          <Link to="/recruit" className="flex items-center gap-2" data-testid="nav-logo">
            <img
              src={scrolled || !isHome ? "/acoe_logo_black.png" : "/acoe_logo_white.png"}
              alt="ACOE"
              className="h-8 md:h-10 w-auto"
            />
            <span className={`text-[10px] font-heading font-medium tracking-[0.3em] uppercase ${textColor} opacity-60`}>RECRUIT</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6" data-testid="nav-desktop-links">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-[13px] tracking-wide font-medium hover:opacity-70 transition-opacity ${textColor}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <Link
              to="/recruit/contact"
              className={`hidden md:inline-flex items-center rounded-full px-6 py-2.5 text-sm font-heading font-semibold tracking-wide transition-colors duration-300 ${
                scrolled || !isHome
                  ? "bg-acoe-charcoal text-acoe-bg hover:bg-acoe-gold"
                  : "border border-acoe-gold text-acoe-gold hover:bg-acoe-gold hover:text-acoe-charcoal"
              }`}
              data-testid="nav-cta-button"
            >
              お問い合わせ
            </Link>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`lg:hidden p-2 ${textColor}`}
              data-testid="nav-hamburger"
              aria-label="メニュー"
            >
              {isOpen ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      </header>

      {isOpen && (
        <div className="fixed inset-0 z-40 bg-acoe-bg flex flex-col pt-20" data-testid="nav-mobile-menu">
          <nav className="flex flex-col px-8 py-8 gap-1">
            <Link to="/recruit" className="py-3 text-lg font-heading font-semibold text-acoe-charcoal border-b border-acoe-border flex items-center gap-2">
              <img src="/acoe_logo_black.png" alt="ACOE" className="h-6 w-auto" /> トップ
            </Link>
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="py-3 text-lg font-heading font-medium text-acoe-charcoal border-b border-acoe-border hover:text-acoe-gold transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="px-8 mt-4">
            <Link
              to="/recruit/contact"
              className="flex items-center justify-center rounded-full bg-acoe-charcoal text-acoe-bg px-8 py-4 text-base font-heading font-semibold tracking-wide hover:bg-acoe-gold transition-colors"
              data-testid="nav-mobile-cta"
            >
              お問い合わせ
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
